import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Eye, EyeOff, X, Check } from "lucide-react";
import { toast } from "sonner";
import Input from "../../../components/input";
import { LockIcon, SignIcon } from "../../../assets/icon";
import PasswordCriteria from "../../../components/password-criteria";
import useRequest from "../../../components/hooks/use-req";

interface ChangePasswordFormData {
    old_password: string;
    new_password: string;
    confirm_password: string;
}

interface ChangePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
    const authToken = localStorage.getItem("authToken");
    const [view, setView] = useState<"form" | "success">("form");
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const { handleSubmit, control, watch, reset } = useForm<ChangePasswordFormData>({
        defaultValues: { old_password: "", new_password: "", confirm_password: "" },
    });

    const { loading, makeRequest } = useRequest("/auth/auth/change-password", "PATCH", {
        Authorization: `Bearer ${authToken}`,
    });

    const newPasswordValue = watch("new_password");

    const handleClose = () => {
        reset();
        setView("form");
        onClose();
    };

    const handleChangePassword = handleSubmit(async (formData) => {
        const [response, error] = await makeRequest({
            old_password: formData.old_password,
            new_password: formData.new_password,
            confirm_password: formData.confirm_password,
        });

        if (response?.status) {
            setView("success");
            toast.success(response?.message || "Password updated successfully");
        } else {
            toast.error(error?.message || "Failed to update password");
        }
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-md p-6 sm:p-8 relative">
                {view === "form" ? (
                    <>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">

                                <SignIcon />

                                <h2 className="text-lg font-bold text-[#121212]">Change Password</h2>
                            </div>
                            <button
                                onClick={handleClose}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500 flex-shrink-0"
                                aria-label="Close"
                                type="button"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <Controller
                                name="old_password"
                                control={control}
                                rules={{ required: "Old password is required" }}
                                render={({ field, fieldState }) => (
                                    <Input
                                        type={showOld ? "text" : "password"}
                                        label="Old Password"
                                        placeholder="Enter your current password"
                                        icon={<LockIcon />}
                                        rightIcon={showOld ? <EyeOff size={18} /> : <Eye size={18} />}
                                        onRightIconClick={() => setShowOld((v) => !v)}
                                        value={field.value}
                                        error={fieldState?.error?.message}
                                        onChange={field.onChange}
                                        className="w-full"
                                    />
                                )}
                            />

                            <div>
                                <Controller
                                    name="new_password"
                                    control={control}
                                    rules={{
                                        required: "New password is required",
                                        pattern: {
                                            value: /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                                            message: "Password must be at least 8 characters long, include a number and a special character",
                                        },
                                    }}
                                    render={({ field, fieldState }) => (
                                        <Input
                                            type={showNew ? "text" : "password"}
                                            label="New Password"
                                            placeholder="Enter your new password"
                                            icon={<LockIcon />}
                                            rightIcon={showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                                            onRightIconClick={() => setShowNew((v) => !v)}
                                            value={field.value}
                                            error={fieldState?.error?.message}
                                            onChange={field.onChange}
                                            className="w-full"
                                        />
                                    )}
                                />
                                {newPasswordValue?.length > 0 && (
                                    <PasswordCriteria password={newPasswordValue} />
                                )}
                            </div>

                            <Controller
                                name="confirm_password"
                                control={control}
                                rules={{
                                    required: "Please confirm your new password",
                                    validate: (value) =>
                                        value === watch("new_password") || "Passwords do not match",
                                }}
                                render={({ field, fieldState }) => (
                                    <Input
                                        type={showConfirm ? "text" : "password"}
                                        label="Confirm Password"
                                        placeholder="Re-enter your new password"
                                        icon={<LockIcon />}
                                        rightIcon={showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                        onRightIconClick={() => setShowConfirm((v) => !v)}
                                        value={field.value}
                                        error={fieldState?.error?.message}
                                        onChange={field.onChange}
                                        className="w-full"
                                    />
                                )}
                            />

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-black text-white rounded-full py-3.5 text-sm font-semibold mt-2 hover:bg-gray-900 transition-colors disabled:opacity-60"
                            >
                                {loading ? "Updating..." : "Update Password"}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="text-center py-2">
                        <h2 className="text-xl font-bold text-[#121212]">Password Updated Successfully</h2>
                        <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                            Your password has been updated successfully. For your security, you'll need to
                            sign in again using your new password.
                        </p>

                        <div className="w-20 h-20 rounded-full border-4 border-blue-100 flex items-center justify-center mx-auto my-6">
                            <Check size={36} className="text-blue-500" />
                        </div>

                        <button
                            onClick={handleClose}
                            className="w-full bg-black text-white rounded-full py-3.5 text-sm font-semibold hover:bg-gray-900 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChangePasswordModal;
